/**
 * The tickets sheet's purchase while the fan chooses and types (spine v4
 * §1, §2): opened as the sheet opens (Get tickets or Register), told what
 * the fan does as they do it, and handed to Buy, which authorises it instead
 * of starting one. A seller not on the spine answers `runner`: nothing more
 * is sent, and Buy starts the purchase as it always has.
 *
 * - Every signal is best effort: one refused or lost never stops the sheet.
 *   They go one at a time, in the order the fan gave them, each with the
 *   capability held then.
 * - `emailConfirmed`'s answer may hold another capability (the fan's older
 *   purchase of the event, kept): it is used from then on, and the pick,
 *   fields and card given so far are told to it.
 * - A purchase that ends before Buy is let go: Buy then starts one as
 *   today. One the seller refused is said in the sheet first (§6).
 * - After Buy the attempt is the purchase, followed as today: nothing more
 *   is told of it but the sheet's beats and its closing, and what it asks
 *   the fan is its attempt view's.
 * - When the fan goes back to choosing after it ended (`renew`): Buy's
 *   attempt ended (declined, stopped, failed, refused), or it ended or was
 *   let go before Buy. A new one opens as the sheet's did, told everything
 *   the fan gave the last, and the next Buy authorises it (Erik, 2 Nov
 *   2026: "if I go back/cancel/fail it should start a new run from the
 *   ticket selection"). Never while Buy's attempt may still run (`over`
 *   says when it is seen to end). One whose opening fails leaves Buy to
 *   start one as today; a seller answered `runner` is never asked again.
 * - Buy waits on the sheet's earlier requests for `AUTHORISE_WAIT_MS` at
 *   most: past it Buy starts the purchase as today, and this one is let go
 *   (told the sheet closed once it can be). The start lets go of one bound
 *   to the fan, so there is never a second.
 * - What the purchase asks the fan (its seller's code, §4.2) comes with
 *   each beat; the sheet asks it before Buy, and its answer is sent naming
 *   the ask. While one is open the sheet beats faster, so the next (a
 *   refused code asked again) shows at once.
 */
import { readAnswers, type Answers, type Field, type StartRefusal } from "@libs/platform-wire/purchase";
import type { PurchaseAsk, PurchaseCapability, PurchaseSignal, PurchaseSignalled, VendorCodeChannel } from "../contracts";
import type { PurchaseOpenInput, PurchaseTransport } from "./transport";

/** How often an open sheet beats: the engine's `ABANDON.heartbeatMs`. Its
 *  answer also says when the purchase ended before Buy, and what it asks. */
export const HEARTBEAT_MS = 5_000;
/** How often it beats while the purchase asks the fan something. */
export const ASKING_HEARTBEAT_MS = 1_500;
/**
 * How long Buy waits on the sheet's earlier requests to our API (the
 * purchase's opening, its binding to the fan, then each signal before Buy,
 * one at a time). Each answers in well under a second, and in two or three
 * on a slow mobile connection; five seconds lets those through and is as
 * long as Buy's spinner should turn before it goes on without them.
 */
export const AUTHORISE_WAIT_MS = 5_000;

type Line = { item_id: string; quantity: number };

/** What Buy carries of the purchase the sheet opened: its id as the start's
 *  own, the sheet's capability and the pick it was quoted. */
export type WarmStart = {
  id: string;
  purchase: { attachmentId: string; secret: string; pickVersion: number | null };
};

export type PurchaseWarmingState = {
  /** The seller said no before Buy (sold out, the price changed), in the
   *  words the sheet says a start refused so with. */
  refusal: StartRefusal | null;
  /** What the purchase asks the fan now (its seller's code), to ask in the
   *  sheet before Buy; null when nothing. */
  ask: PurchaseAsk | null;
};

/** The words of a refusal before Buy, as the server words a start refused
 *  with the same code (its `views/refusal.ts`): every one is the seller
 *  saying no to the order as chosen. */
export function earlyRefusalOf(reason: string | null, seller: { name: string }): StartRefusal {
  const code = reason ?? "offer_changed";
  if (code === "journey_unavailable")
    return {
      code,
      title: "Not on sale through Lissen",
      message: "This isn't on sale through Lissen right now.",
      fieldId: null,
      problems: [],
    };
  // Insurance the seller sells the tickets only with, which Lissen never
  // buys: its order did not change, so it is not said to have (Erik, 2 Nov
  // 2026: such a checkout ends as the seller's refusal).
  if (code === "insurance_required")
    return {
      code,
      title: `${seller.name} only sells these with its insurance`,
      message: `Nothing was paid. You can buy them on ${seller.name} directly.`,
      fieldId: null,
      problems: [],
    };
  return {
    code,
    title: "Your order changed",
    message: `Prices or what ${seller.name} has left changed. Check your order and try again.`,
    fieldId: null,
    problems: [],
  };
}

/** The engine's names for what the purchase's fields are bound to (§2's
 *  open list of fields), by the answers' own keys (`readAnswers`): the
 *  title is the engine's `civility`. */
const ENGINE_NAMES: Readonly<Record<string, string>> = {
  firstName: "firstName",
  lastName: "lastName",
  dateOfBirth: "dateOfBirth",
  phone: "mobile",
  country: "country",
  region: "region",
  postcode: "postcode",
  address: "street",
  address2: "street2",
  city: "city",
  identityDocument: "identityDocument",
  title: "civility",
  nationality: "nationality",
  discountCode: "discountCode",
};

/** An address's parts as the engine names them, as typed. */
const ADDRESS_PARTS = { line1: "street", line2: "street2", city: "city", region: "region", postcode: "postcode" } as const;

/**
 * What one field's answer tells the engine, by its names: read as the start
 * reads it (`readAnswers`), so a mobile number is the one dialled and a date
 * is ISO. An address not yet whole tells its country and each part typed so
 * far. A field the engine has no name for tells nothing.
 */
export function warmFieldsOf(field: Field, answer: Answers[number]["answer"]): { name: string; value: string }[] {
  const read = readAnswers([field], [{ id: field.id, answer }]);
  if (read.ok)
    return Object.entries({ ...read.execution, ...read.values }).flatMap(([key, value]) =>
      ENGINE_NAMES[key] && typeof value === "string" && value ? [{ name: ENGINE_NAMES[key], value }] : [],
    );
  if (field.kind !== "address" || !answer || !("country" in answer)) return [];
  const address = answer as { country: string } & Partial<Record<keyof typeof ADDRESS_PARTS, string | null>>;
  return [
    { name: "country", value: address.country },
    ...Object.entries(ADDRESS_PARTS).flatMap(([part, name]) => {
      const value = address[part as keyof typeof ADDRESS_PARTS]?.trim();
      return value ? [{ name, value }] : [];
    }),
  ];
}

export function createPurchaseWarming(
  transport: Pick<PurchaseTransport, "open" | "signal">,
  options: PurchaseOpenInput & {
    /** Whose words an early refusal says. */
    seller: { name: string };
    /** Runs `beat` every `ms` until the returned function is called. */
    every?: (beat: () => void, ms: number) => () => void;
    /** How long Buy waits on the requests before it (`AUTHORISE_WAIT_MS`). */
    authoriseWaitMs?: number;
  },
) {
  const every =
    options.every ??
    ((beat: () => void, ms: number) => {
      const timer = setInterval(beat, ms);
      return () => clearInterval(timer);
    });
  let state: PurchaseWarmingState = { refusal: null, ask: null };
  const listeners = new Set<() => void>();
  let capability: PurchaseCapability | null = null;
  let closed = false;
  /** The seller answered `runner`: nothing more is sent, ever. */
  let runner = false;
  /** An opening not yet answered. */
  let pending = false;
  /** Buy authorised it: the attempt is followed as today from now. */
  let bought = false;
  /** Buy went on without it: it is let go. */
  let gaveUp = false;
  /** Buy was pressed and what it started is not yet seen to be over. */
  let buying = false;
  let stopBeating: (() => void) | undefined;
  /** The signals in flight, one at a time, in order. */
  let queue: Promise<unknown> = Promise.resolve();
  let pick: { key: string; lines: Line[]; country?: string; version: number | null } | null = null;
  const fields = new Map<string, string>();
  let card: string | null = null;
  /** The fan's confirmed email bound the purchase to them. */
  let bound = false;
  let confirming: Promise<void> | null = null;
  /** The sheet said the fan's email is confirmed: a purchase opened again
   *  is told so too. */
  let confirmed = false;
  /** Which of the sheet's purchases it is: an answer about an earlier one
   *  binds nothing. */
  let epoch = 0;

  const set = (patch: Partial<PurchaseWarmingState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  /** Let go: nothing more is told of it, and Buy starts as today. */
  const drop = () => {
    capability = null;
    stopBeating?.();
    stopBeating = undefined;
    if (state.ask) set({ ask: null });
  };
  /** It ended: before Buy, a refusal is the sheet's to say. */
  const ended = (held: PurchaseCapability, ending: PurchaseSignalled["ending"]) => {
    if (capability !== held) return;
    drop();
    // The attempt Buy made of it ended.
    if (bought) buying = false;
    if (!bought && ending?.kind === "refused") set({ refusal: earlyRefusalOf(ending.reason, options.seller) });
  };
  /** What an answer says the purchase asks now, from this capability's. */
  const asked = (held: PurchaseCapability, answer: PurchaseSignalled) => {
    // After Buy the attempt view asks it.
    if (capability !== held || answer.ask === undefined || bought) return;
    const next = answer.ask;
    if (JSON.stringify(next) === JSON.stringify(state.ask)) return;
    const pace = (next !== null) !== (state.ask !== null);
    set({ ask: next });
    if (pace) beat();
  };
  /** Beats at the pace of what the purchase asks: faster while it asks. */
  const beat = () => {
    stopBeating?.();
    stopBeating = every(() => {
      const held = capability;
      if (!held) return;
      transport
        .signal(held, { kind: "heartbeat" })
        .then((answer) => {
          if (!answer.open) ended(held, answer.ending);
          else asked(held, answer);
        })
        .catch(() => undefined);
    }, state.ask ? ASKING_HEARTBEAT_MS : HEARTBEAT_MS);
  };
  /** Buy goes on without it: nothing more is told of it, and it hears the
   *  sheet closed once it can (its opening answered), so its driver lets it
   *  go before it is ever bought. */
  const letGo = () => {
    gaveUp = true;
    const held = capability;
    drop();
    if (held && !closed) transport.signal(held, { kind: "sheet.closed" }).catch(() => undefined);
  };

  /** Opens a purchase for the sheet; one at a time. */
  const open = (input: PurchaseOpenInput) => {
    pending = true;
    return (async () => {
      try {
        const opened = await transport.open(input);
        if (opened.engine !== "spine") {
          runner = true;
          return;
        }
        capability = { purchaseId: opened.purchaseId, attachmentId: opened.attachmentId, secret: opened.secret };
        // Closed while it opened: `close` tells it so, and nothing beats.
        if (closed) return;
        // Buy went on without it while it opened: let go at once.
        if (gaveUp) return letGo();
        beat();
      } catch {
        /* Buy starts it, as today. */
      } finally {
        pending = false;
      }
    })();
  };
  let opening = open({
    visitorId: options.visitorId, accessId: options.accessId, listingId: options.listingId,
    ...(options.pageOpenedAt ? { pageOpenedAt: options.pageOpenedAt } : {}),
  });

  /** The card, once the purchase is the fan's: only theirs takes it. */
  const tellCard = () => {
    if (card && bound) send({ kind: "card", cardId: card }).catch(() => undefined);
  };
  /** Another capability from `emailConfirmed`: the fan's own, bound, told
   *  what this sheet has given so far. */
  const replace = (next: PurchaseCapability) => {
    capability = next;
    bound = true;
    if (pick) {
      pick.version = null;
      pickSignal(pick).catch(() => undefined);
    }
    for (const [name, value] of fields) send({ kind: "field", name, value }).catch(() => undefined);
    tellCard();
  };
  /** One signal, after those before it, with the capability held then;
   *  null where none was sent or it was not answered. */
  const send = (signal: PurchaseSignal): Promise<PurchaseSignalled | null> => {
    // Told about the purchase open now: one renewed meanwhile is told
    // nothing of the last one's.
    const about = epoch;
    const run = async () => {
      await opening;
      const held = capability;
      if (!held || closed || about !== epoch) return null;
      try {
        const answer = await transport.signal(held, signal);
        if (answer.capability && capability === held && answer.capability.secret !== held.secret) replace(answer.capability);
        if (!answer.open) ended(held, answer.ending);
        else asked(held, answer);
        return answer;
      } catch {
        // Best effort: the next signal, or Buy, says it again.
        return null;
      }
    };
    const result = queue.then(run);
    queue = result.catch(() => undefined);
    return result;
  };
  const pickSignal = async (sent: NonNullable<typeof pick>) => {
    const answer = await send({ kind: "picked", lines: sent.lines, ...(sent.country ? { country: sent.country } : {}) });
    if (pick === sent && answer?.version !== undefined) sent.version = answer.version;
  };
  const live = () => !closed && !bought && !gaveUp;

  const controller = {
    snapshot: () => state,
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    /** The basket as the fan has it now, at every change; `country` is
     *  where it is priced for. */
    picked(lines: readonly Line[], country?: string) {
      if (!live() || !lines.length) return;
      const key = JSON.stringify([lines, country ?? pick?.country ?? null]);
      if (pick?.key === key) return;
      pick = { key, lines: lines.map((line) => ({ ...line })), country: country ?? pick?.country, version: null };
      pickSignal(pick).catch(() => undefined);
    },
    /** One field, by its engine name, as the fan left it. */
    field(name: string, value: string) {
      const trimmed = value.trim();
      if (!live() || !trimmed || fields.get(name) === trimmed) return;
      fields.set(name, trimmed);
      send({ kind: "field", name, value: trimmed }).catch(() => undefined);
    },
    /** The card that will pay is complete: a saved one with its code fresh,
     *  or a new one captured. Told once the fan's email is confirmed. */
    card(cardId: string) {
      if (!live() || card === cardId) return;
      card = cardId;
      tellCard();
    },
    /** The fan is signed in with a verified email: the purchase is bound to
     *  them. Once; again only after it went unanswered. */
    emailConfirmed(): Promise<void> {
      if (!live() || bound) return Promise.resolve();
      confirmed = true;
      const asked = epoch;
      confirming ??= send({ kind: "emailConfirmed" }).then((answer) => {
        if (asked !== epoch) return;
        if (!answer?.open) {
          confirming = null;
          return;
        }
        // Bound already where it answered with the fan's older purchase,
        // which was told the card then.
        const told = bound;
        bound = true;
        if (!told) tellCard();
      });
      return confirming;
    },
    /**
     * What Buy carries for this purchase, once everything told before it is
     * answered and the basket it buys is the pick told last; null where
     * there is none to authorise (a seller not on the spine, a purchase let
     * go or never bound to the fan), and Buy starts one as today. Null too
     * once those requests took `AUTHORISE_WAIT_MS`: it is let go.
     */
    async authorise(lines: readonly Line[]): Promise<WarmStart | null> {
      buying = true;
      const told = (async () => {
        await opening;
        if (!capability || !live()) return false;
        if (!bound) await controller.emailConfirmed();
        controller.picked(lines);
        await queue;
        return true;
      })();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const waited = await Promise.race([
        told,
        new Promise<"late">((resolve) => {
          timer = setTimeout(() => resolve("late"), options.authoriseWaitMs ?? AUTHORISE_WAIT_MS);
        }),
      ]).finally(() => clearTimeout(timer));
      if (waited === "late") {
        letGo();
        return null;
      }
      if (!waited) return null;
      const held = capability;
      if (!held || !bound || !live()) return null;
      return {
        id: held.purchaseId,
        purchase: { attachmentId: held.attachmentId, secret: held.secret, pickVersion: pick?.version ?? null },
      };
    },
    /** The fan's answer to what the purchase asks (`state.ask`): the code
     *  typed in its field. False when it was not taken (the ask closed, the
     *  answer refused or lost): the next beat says what is asked now. */
    async answer(answers: Answers): Promise<boolean> {
      const ask = state.ask;
      if (!ask || ask.screen.answered || closed) return false;
      const answer = await send({ kind: "answer", promptId: ask.promptId, answers });
      return answer !== null;
    },
    /** Asks the seller to send its code another way the ask offers. */
    async resend(channel: VendorCodeChannel): Promise<boolean> {
      const ask = state.ask;
      if (!ask || ask.screen.answered || closed) return false;
      return (await send({ kind: "resend", promptId: ask.promptId, channel })) !== null;
    },
    /** Buy's start was adopted: the attempt is followed as today, and
     *  asks the fan through its view. */
    started() {
      bought = true;
      if (state.ask) set({ ask: null });
    },
    /** Buy was refused for the purchase itself (not found, not the fan's):
     *  the next Buy starts one as today. */
    lapse() {
      const held = capability;
      drop();
      if (held && !closed) transport.signal(held, { kind: "sheet.closed" }).catch(() => undefined);
    },
    /** Buy's attempt is over: its view says it ended (`id`), or the start
     *  was refused. One this purchase became is let go: nothing more is
     *  told of it. */
    over(id?: string) {
      buying = false;
      if (id && bought && capability?.purchaseId === id) drop();
    },
    /**
     * The fan is back on choosing after the purchase ended (its attempt
     * after Buy, or the purchase before it): a new one opens as the sheet's
     * did and is told the pick, the confirmed email, the fields and the card
     * the fan gave, in that order. Nothing while one is open or opening (the
     * sheet's still warming, or Buy's not yet over: a purchase confirming
     * with its seller while the fan looks back at the tickets), once the
     * sheet closed, or where the seller is not on the spine.
     */
    renew() {
      if (closed || runner || pending || buying || capability) return;
      epoch++;
      bought = false;
      gaveUp = false;
      bound = false;
      confirming = null;
      // Its own queue: a request about the last purchase still unanswered
      // holds up nothing of this one.
      queue = Promise.resolve();
      if (state.refusal) set({ refusal: null });
      // Not the page's opening: the fan's first action in this one is
      // going back.
      opening = open({ visitorId: options.visitorId, accessId: options.accessId, listingId: options.listingId });
      if (pick) {
        // A copy: a late answer about the last purchase's pick sets no
        // version on this one's.
        pick = { ...pick, version: null };
        pickSignal(pick).catch(() => undefined);
      }
      if (confirmed) controller.emailConfirmed().catch(() => undefined);
      for (const [name, value] of fields) send({ kind: "field", name, value }).catch(() => undefined);
    },
    /** The sheet closed, or the page is going: one last signal that may
     *  outlive the page. */
    close() {
      if (closed) return;
      closed = true;
      stopBeating?.();
      opening
        .then(() => {
          const held = capability;
          if (held) return transport.signal(held, { kind: "sheet.closed" }, { keepalive: true });
        })
        .catch(() => undefined);
    },
  };
  return controller;
}
export type PurchaseWarming = ReturnType<typeof createPurchaseWarming>;
